import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Table, Button, Space, Select, Tag, Modal, Form, Input, message, Row, Col, Typography, Popconfirm, Tooltip,
} from 'antd';
import {
  TagsOutlined, PlusOutlined, SearchOutlined, SyncOutlined, EditOutlined, DeleteOutlined, FilterOutlined,
} from '@ant-design/icons';
import {
  TagEntity, getTagList, getTagsByType, searchTags, createTag, updateTag, deleteTag,
  TAG_TYPES, TAG_SOURCES, getTagTypeLabel, getTagSourceLabel,
} from '../services/tagApi';

const { Title, Text } = Typography;

// 商务风格配色
const COLORS = {
  primary: '#1677ff',
  secondary: '#722ed1',
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  // 兼容旧代码的别名
  cyan: '#1677ff',
  purple: '#722ed1',
  pink: '#ff4d4f',
  green: '#52c41a',
  orange: '#faad14',
  yellow: '#faad14',
  darkBg: '#f5f5f5',
  cardBg: '#ffffff',
  borderDark: '#f0f0f0',
};

// 简洁页面标题组件
const PageHeader: React.FC<{ icon: React.ReactNode; title: string; subtitle?: string; color?: string }> = ({ icon, title, subtitle, color = COLORS.primary }) => (
  <div style={{ marginBottom: 24 }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
      <div style={{
        width: 48, height: 48,
        background: `${color}10`,
        borderRadius: 8,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 24, color: color,
      }}>{icon}</div>
      <div>
        <Title level={2} style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>{title}</Title>
        {subtitle && <Text style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 14 }}>{subtitle}</Text>}
      </div>
    </div>
    <div style={{
      width: 120, height: 3, marginLeft: 72,
      background: `linear-gradient(90deg, ${color}, ${color}66, transparent)`,
      borderRadius: 2,
    }} />
  </div>
);

// 简洁卡片组件
const SimpleCard: React.FC<{ title?: React.ReactNode; extra?: React.ReactNode; children: React.ReactNode; style?: React.CSSProperties; accentColor?: string; noPadding?: boolean }> = ({ title, extra, children, style, accentColor = COLORS.primary, noPadding }) => (
  <Card
    title={title ? <Text style={{ fontWeight: 600, fontSize: 16 }}>{title}</Text> : undefined}
    extra={extra}
    style={{
      background: COLORS.cardBg,
      border: `1px solid ${COLORS.borderDark}`,
      borderRadius: 12,
      overflow: 'hidden',
      ...style,
    }}
    styles={{
      header: title ? {
        borderBottom: `1px solid ${COLORS.borderDark}`,
        background: `${accentColor}08`,
      } : undefined,
      body: { padding: noPadding ? 0 : 24 },
    }}
  >
    {children}
  </Card>
);

// 标签类型颜色映射
const getTypeDisplayColor = (tagType: string): string => {
  switch (tagType) {
    case 'topic': return COLORS.cyan;
    case 'sentiment': return COLORS.pink;
    case 'keyword': return COLORS.green;
    case 'category': return COLORS.orange;
    default: return '#6b7280';
  }
};

const TagManagement: React.FC = () => {
  const [tags, setTags] = useState<TagEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [filterType, setFilterType] = useState<string>('');
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [editingTag, setEditingTag] = useState<TagEntity | null>(null);
  const [form] = Form.useForm();

  const fetchTags = useCallback(async () => {
    setLoading(true);
    try {
      let result: TagEntity[];
      if (searchKeyword) {
        result = await searchTags(searchKeyword);
      } else if (filterType) {
        result = await getTagsByType(filterType);
      } else {
        result = await getTagList();
      }
      setTags(result);
    } catch (e: any) {
      message.error('加载失败: ' + e.message);
      setTags([]);
    } finally {
      setLoading(false);
    }
  }, [filterType, searchKeyword]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  const handleCreate = () => {
    setEditingTag(null);
    form.resetFields();
    form.setFieldsValue({ tagType: 'topic', source: 'manual' });
    setShowModal(true);
  };

  const handleEdit = (record: TagEntity) => {
    setEditingTag(record);
    form.setFieldsValue({
      name: record.name,
      tagType: record.tagType,
      description: record.description,
      source: record.source,
    });
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await deleteTag(id);
      message.success('删除成功');
      fetchTags();
    } catch (e: any) {
      message.error('删除失败: ' + e.message);
    }
  };

  const handleSubmit = async (values: any) => {
    try {
      if (editingTag) {
        await updateTag(editingTag.id, {
          name: values.name,
          description: values.description,
        });
        message.success('更新成功');
      } else {
        await createTag({
          name: values.name,
          tagType: values.tagType,
          description: values.description,
          source: values.source,
        });
        message.success('创建成功');
      }
      setShowModal(false);
      form.resetFields();
      fetchTags();
    } catch (e: any) {
      message.error('操作失败: ' + e.message);
    }
  };

  const handleSearch = () => {
    fetchTags();
  };

  const getTypeTag = (tagType: string) => {
    const color = getTypeDisplayColor(tagType);
    return (
      <Tag style={{
        background: `${color}22`,
        border: `1px solid ${color}44`,
        color: color,
        borderRadius: 6,
      }}>
        {getTagTypeLabel(tagType)}
      </Tag>
    );
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
      render: (id: number) => (
        <Text style={{ color: '#6e7681', fontFamily: 'monospace' }}>#{id}</Text>
      ),
    },
    {
      title: '标签名称',
      dataIndex: 'name',
      key: 'name',
      render: (name: string, record: TagEntity) => {
        const color = getTypeDisplayColor(record.tagType);
        return (
          <Tag style={{
            background: `${color}15`,
            border: `1px solid ${color}33`,
            color: color,
            borderRadius: 8,
            padding: '4px 12px',
            fontSize: 14,
            fontWeight: 500,
          }}>
            {name}
          </Tag>
        );
      },
    },
    {
      title: '类型',
      dataIndex: 'tagType',
      key: 'tagType',
      width: 120,
      render: (tagType: string) => getTypeTag(tagType),
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (desc: string) => (
        <Tooltip title={desc}>
          <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>{desc || '-'}</Text>
        </Tooltip>
      ),
    },
    {
      title: '来源',
      dataIndex: 'source',
      key: 'source',
      width: 100,
      render: (source: string) => (
        <Text style={{ color: '#6e7681', fontSize: 12 }}>{getTagSourceLabel(source)}</Text>
      ),
    },
    {
      title: '使用次数',
      dataIndex: 'usageCount',
      key: 'usageCount',
      width: 100,
      sorter: (a: TagEntity, b: TagEntity) => a.usageCount - b.usageCount,
      render: (count: number) => (
        <Text style={{ color: COLORS.cyan, fontWeight: 500 }}>{count || 0}</Text>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'createTime',
      key: 'createTime',
      width: 160,
      render: (time: string) => (
        <Text style={{ color: '#6e7681', fontSize: 12 }}>
          {time ? new Date(time).toLocaleString('zh-CN') : '-'}
        </Text>
      ),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_: any, record: TagEntity) => (
        <Space>
          <Tooltip title="编辑">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={() => handleEdit(record)}
              style={{ color: COLORS.cyan }}
            />
          </Tooltip>
          <Popconfirm
            title="确认删除"
            description={`确定要删除标签 "${record.name}" 吗？`}
            onConfirm={() => handleDelete(record.id)}
            okText="确认"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button
                type="text"
                icon={<DeleteOutlined />}
                style={{ color: COLORS.pink }}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      <PageHeader
        icon={<TagsOutlined />}
        title="标签管理"
        subtitle="管理系统标签、话题、情感和分类标签"
        color={COLORS.cyan}
      />

      {/* 筛选区域 */}
      <SimpleCard style={{ marginBottom: 24 }} accentColor={COLORS.cyan}>
        <Row gutter={[16, 16]} align="middle">
          <Col>
            <Space>
              <FilterOutlined style={{ color: COLORS.cyan }} />
              <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>筛选:</Text>
            </Space>
          </Col>
          <Col>
            <Space>
              <Text style={{ color: '#6e7681', fontSize: 13 }}>类型</Text>
              <Select
                value={filterType}
                onChange={(v) => {
                  setFilterType(v);
                  setSearchKeyword('');
                }}
                style={{ width: 140 }}
                allowClear
                placeholder="全部类型"
                options={[
                  { value: '', label: '全部' },
                  ...TAG_TYPES.map((t) => ({ value: t.value, label: t.label })),
                ]}
              />
            </Space>
          </Col>
          <Col flex={1}>
            <Space>
              <Input
                placeholder="搜索标签名称..."
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onPressEnter={handleSearch}
                style={{ width: 240 }}
                prefix={<SearchOutlined style={{ color: '#6e7681' }} />}
                allowClear
              />
              <Button
                onClick={handleSearch}
                icon={<SyncOutlined />}
                type="primary"
              >
                查询
              </Button>
            </Space>
          </Col>
          <Col>
            <Button
              icon={<PlusOutlined />}
              onClick={handleCreate}
              type="primary"
              style={{ background: COLORS.success, borderColor: COLORS.success }}
            >
              新建标签
            </Button>
          </Col>
        </Row>
      </SimpleCard>

      {/* 标签列表 */}
      <SimpleCard
        title={
          <Space>
            <TagsOutlined style={{ color: COLORS.cyan }} />
            <span>标签列表</span>
            <Tag style={{
              background: `${COLORS.cyan}15`,
              border: `1px solid ${COLORS.cyan}33`,
              color: COLORS.cyan,
            }}>
              {tags.length}
            </Tag>
          </Space>
        }
        accentColor={COLORS.cyan}
        noPadding
      >
        <Table
          columns={columns}
          dataSource={tags}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => <Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>共 {total} 条</Text>,
          }}
          style={{ background: 'transparent' }}
        />
      </SimpleCard>

      {/* 新建/编辑弹窗 */}
      <Modal
        title={
          <Space>
            {editingTag ? <EditOutlined style={{ color: COLORS.cyan }} /> : <PlusOutlined style={{ color: COLORS.green }} />}
            <span style={{ color: 'rgba(0, 0, 0, 0.85)' }}>{editingTag ? '编辑标签' : '新建标签'}</span>
          </Space>
        }
        open={showModal}
        onCancel={() => {
          setShowModal(false);
          form.resetFields();
        }}
        footer={null}
        styles={{ body: { paddingTop: 24 } }}
      >
        <Form form={form} onFinish={handleSubmit} layout="vertical">
          <Form.Item
            label={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>标签名称</Text>}
            name="name"
            rules={[{ required: true, message: '请输入标签名称' }]}
          >
            <Input placeholder="请输入标签名称" />
          </Form.Item>
          <Form.Item
            label={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>标签类型</Text>}
            name="tagType"
            rules={[{ required: true, message: '请选择标签类型' }]}
          >
            <Select
              options={TAG_TYPES.map((t) => ({ value: t.value, label: t.label }))}
              disabled={!!editingTag}
              placeholder="请选择标签类型"
            />
          </Form.Item>
          <Form.Item
            label={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>描述</Text>}
            name="description"
          >
            <Input.TextArea rows={3} placeholder="请输入标签描述（可选）" />
          </Form.Item>
          {!editingTag && (
            <Form.Item
              label={<Text style={{ color: 'rgba(0, 0, 0, 0.45)' }}>来源</Text>}
              name="source"
            >
              <Select
                options={TAG_SOURCES.map((s) => ({ value: s.value, label: s.label }))}
                placeholder="请选择来源"
              />
            </Form.Item>
          )}
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setShowModal(false)}>取消</Button>
              <Button
                type="primary"
                htmlType="submit"
              >
                {editingTag ? '保存' : '创建'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default TagManagement;
